import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { Notification, SuccessOutput } from '@shared/models';
import { CookieService } from '@shared/services/cookie.service';
import { AuthService } from '@shared/services/auth.service';
import { first, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private hub: signalR.HubConnection | null = null;

  private readonly _notifications = signal<Notification[]>([]);
  private readonly _isConnected = signal(false);

  readonly notifications = this._notifications.asReadonly();
  readonly isConnected = this._isConnected.asReadonly();
  readonly unreadCount = computed(() =>
    this._notifications().filter((n) => !n.isRead).length
  );

  private readonly api = `${environment.baseUrl}/notifications`;
  private isConnecting = false;

  constructor(
    private cookieService: CookieService,
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  async startConnection(tenantId: string): Promise<void> {
    if (
      this.isConnecting ||
      (this.hub && this.hub.state !== signalR.HubConnectionState.Disconnected)
    ) {
      return;
    }

    this.isConnecting = true;

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.notificationsHubUrl}?tenantId=${tenantId}`, {
        accessTokenFactory: () => this.cookieService.getCookie('access_token') ?? '',
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    this.hub.on('ReceiveNotification', (data: Notification) => {
      this._notifications.update((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return [{ ...data, isRead: false }, ...list];
      });
    });

    this.hub.onreconnected(() => this._isConnected.set(true));
    this.hub.onclose(() => this._isConnected.set(false));

    try {
      await this.hub.start();
      this._isConnected.set(true);
    } catch (err) {
      console.error('[SignalR] Erro ao conectar:', err);
      this.hub = null;
    } finally {
      this.isConnecting = false;
    }
  }

  markAsRead(notificationIds: number[]): void {
    this.http.put(`${this.api}/read-status`, {notificationIds: notificationIds}).pipe(first()).subscribe({
    next: () => {
      this._notifications.update(notifications => 
        notifications.map(n => 
          notificationIds.includes(n.notificationId) ? { ...n, isRead: true } : n
        )
      );
    },
    error: (err) => console.error('Erro ao marcar como lida:', err)
  });
  }

  clearAll(): void {
    this._notifications.set([]);
  }

  async stopConnection(): Promise<void> {
    if (this.hub) {
      this.hub.off('ReceiveNotification');
      if (this.hub.state !== signalR.HubConnectionState.Disconnected) {
        await this.hub.stop();
      }
      this.hub = null;
    }
    this._isConnected.set(false);
  }

  ngOnDestroy(): void {
    this.stopConnection();
  }

  getAllNotifications(userId: string): Observable<SuccessOutput<Notification[]>> {
    return this.http.get<SuccessOutput<Notification[]>>(this.api, {
      params: { userId },
    });
  }

  loadAll(): void {
    const userId = this.authService.currentUser()?.id;
    if (!userId) return;
    this.getAllNotifications(userId).subscribe({
      next: (response) => this._notifications.set(response.data ?? []),
    });
  }
}

